"use client";

import { useEffect } from "react";
import { recordRumError } from "@/lib/rum/client";

/**
 * Error boundary for the localised app tree — the one that will actually
 * catch most render errors, since nearly every route lives under [locale].
 *
 * Reports to RUM for the same reason as global-error: a render error caught
 * by a React boundary never reaches window.onerror, so RUM's error telemetry
 * cannot see it on its own.
 *
 * Kept free of next-intl translations deliberately: this boundary sits inside
 * NextIntlClientProvider, so a failure in the i18n tree would make a
 * translated fallback fail too.
 */
export default function LocaleError({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  useEffect(() => {
    recordRumError(error);
  }, [error]);

  return (
    <main
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        minHeight: "60vh",
        padding: "2rem",
        textAlign: "center",
      }}
    >
      <div style={{ maxWidth: "32rem" }}>
        <h1 style={{ fontSize: "1.25rem", marginBottom: "0.75rem" }}>
          Something went wrong
        </h1>
        <p style={{ margin: "0 0 1.5rem", lineHeight: 1.5 }}>
          This page could not be displayed. The problem has been reported.
        </p>
        <button
          type="button"
          onClick={reset}
          style={{
            padding: "0.625rem 1.25rem",
            fontSize: "1rem",
            cursor: "pointer",
            color: "#ffffff",
            background: "#EB1700",
            border: "none",
            borderRadius: "0.25rem",
          }}
        >
          Try again
        </button>
        {error.digest && (
          <p style={{ marginTop: "1.5rem", fontSize: "0.75rem", color: "#6b6b6b" }}>
            Reference: {error.digest}
          </p>
        )}
      </div>
    </main>
  );
}
